/**
 * IssuingCourtCard — logs the specific court(s) a matter is being issued
 * at. Litigation can run against BOTH the company and the
 * director/guarantor, who may sit in different jurisdictions, so the card
 * tracks one court per party:
 *
 *   • Company            — auto-found from the Companies House registered
 *                          office address
 *   • Guarantor/Director — auto-found from the LoanDisk borrower/guarantor
 *                          address (with a source picker when the case has
 *                          multiple guarantors)
 *
 * Each slot supports: auto Find / Re-find (fresh address pull, catches
 * change-of-address), manual postcode search fallback, Change and Clear.
 * Selections persist to `collections_cases.issuing_courts.{party}` with an
 * audit note on the case timeline.
 */
import { useState, useEffect } from "react";
import {
  Landmark, ExternalLink, Trash2, Loader2, Building2, User, MapPin,
  RefreshCw, Search, X, Pencil, Plus, Hash,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { setCaseIssuingCourt, clearCaseIssuingCourt, getCourtSuggestions, patchCaseIssuingCourtMeta } from "@/lib/api";
import CourtPicker, { formatCourtAddress } from "./CourtPicker";

const EXTRA_GUARANTOR_SLOTS = ["guarantor2", "guarantor3"];

export default function IssuingCourtCard({ caseRow, onChanged }) {
  const courts = caseRow.issuing_courts || {};
  // Extra PG slots appear once used, or when the handler adds one
  const [extra, setExtra] = useState(() => EXTRA_GUARANTOR_SLOTS.filter((k) => courts[k]?.name));
  const visibleExtra = EXTRA_GUARANTOR_SLOTS.filter((k) => courts[k]?.name || extra.includes(k));
  const nextSlot = EXTRA_GUARANTOR_SLOTS.find((k) => !visibleExtra.includes(k));
  return (
    <Card data-testid="issuing-court-card">
      <CardHeader className="py-3 px-4 border-b">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <Landmark className="w-4 h-4 text-red-700" />
          Issuing Courts
        </CardTitle>
        <p className="text-[11px] text-muted-foreground font-normal mt-0.5">
          Company and each guarantor can be litigated in different jurisdictions — log each court separately, label it with the PG it relates to and record the claim / case number.
        </p>
      </CardHeader>
      <CardContent className="p-0 divide-y">
        <CourtSlot
          party="company"
          label="Company"
          icon={Building2}
          findLabel="Find from Companies House"
          court={courts.company?.name ? courts.company : null}
          caseRow={caseRow}
          onChanged={onChanged}
        />
        <CourtSlot
          party="guarantor"
          label="Guarantor / Director"
          icon={User}
          findLabel="Find from LoanDisk address"
          court={courts.guarantor?.name ? courts.guarantor : null}
          caseRow={caseRow}
          onChanged={onChanged}
        />
        {visibleExtra.map((k, i) => (
          <CourtSlot
            key={k}
            party={k}
            label={`Guarantor ${i + 2}`}
            icon={User}
            findLabel="Find from LoanDisk address"
            court={courts[k]?.name ? courts[k] : null}
            caseRow={caseRow}
            onChanged={onChanged}
          />
        ))}
        {nextSlot && (
          <div className="p-3">
            <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => setExtra((x) => [...x, nextSlot])} data-testid="issuing-court-add-guarantor">
              <Plus className="w-3 h-3 mr-1" /> Add another guarantor court (second PG)
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// One party's court slot — idle / suggesting / manual-search states.
function CourtSlot({ party, label, icon: Icon, findLabel, court, caseRow, onChanged }) {
  const [busy, setBusy]               = useState(false);
  const [finding, setFinding]         = useState(false);
  const [suggestions, setSuggestions] = useState(null); // {source, sources, courts}
  const [manual, setManual]           = useState(false);

  const find = async (borrowerId = null) => {
    setFinding(true);
    setManual(false);
    try {
      const r = await getCourtSuggestions(caseRow.case_id, party, borrowerId);
      if (!r.ok) {
        toast.error(r.message || "Couldn't auto-find an address — search manually.");
        setSuggestions(r.sources?.length ? { ...r, courts: [] } : null);
        if (!r.sources?.length) setManual(true);
      } else {
        setSuggestions(r);
        if (!r.courts?.length) toast.info("No courts returned for that postcode — try a manual search.");
      }
    } catch (e) {
      toast.error("Court lookup failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setFinding(false);
    }
  };

  const save = async (selected, sourceMeta = null) => {
    setBusy(true);
    try {
      const payload = { ...selected };
      if (sourceMeta) {
        payload.source_label = sourceMeta.label;
        payload.source_address = sourceMeta.address;
      }
      await setCaseIssuingCourt(caseRow.case_id, payload, party);
      toast.success(`${label} issuing court set to ${selected.name}`);
      setSuggestions(null);
      setManual(false);
      await onChanged?.();
    } catch (e) {
      toast.error("Failed to save court: " + (e?.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    if (!window.confirm(`Clear ${court?.name} as the ${label.toLowerCase()} issuing court?`)) return;
    setBusy(true);
    try {
      await clearCaseIssuingCourt(caseRow.case_id, party);
      toast.success(`${label} issuing court cleared`);
      await onChanged?.();
    } catch (e) {
      toast.error("Failed to clear court: " + (e?.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-4 space-y-2" data-testid={`issuing-court-slot-${party}`}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 flex items-center gap-1.5">
          <Icon className="w-3.5 h-3.5" /> {label}
          {(busy || finding) && <Loader2 className="w-3 h-3 animate-spin" />}
        </span>
        {court && (
          <span className="flex items-center gap-1">
            <Button size="sm" variant="ghost" className="h-6 text-[10px] px-1.5" onClick={() => find()} disabled={finding} title="Re-pull the latest address and re-find courts (change of address)" data-testid={`issuing-court-refind-${party}`}>
              <RefreshCw className="w-3 h-3 mr-1" /> Re-find
            </Button>
            <Button size="sm" variant="ghost" className="h-6 text-[10px] px-1.5 text-rose-600 hover:text-rose-700" onClick={clear} disabled={busy} data-testid={`issuing-court-clear-${party}`}>
              <Trash2 className="w-3 h-3" />
            </Button>
          </span>
        )}
      </div>

      {/* Selected court display */}
      {court && !suggestions && !manual && (
        <div data-testid={`issuing-court-selected-${party}`}>
          <CourtMeta party={party} court={court} caseRow={caseRow} onChanged={onChanged} />
          <div className="font-semibold text-sm text-slate-900 flex items-center gap-1.5">
            {court.name}
            {court.manual && (
              <span className="text-[9px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded border border-amber-300 bg-amber-50 text-amber-800" data-testid={`issuing-court-manual-pill-${party}`} title={court.manual_note || "Entered manually by the handler"}>
                manual
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">{formatCourtAddress(court) || "—"}</div>
          {court.manual_note && <div className="text-[10px] text-slate-600 italic mt-0.5">{court.manual_note}</div>}
          <div className="text-[11px] text-slate-600 mt-1 space-x-2">
            {court.dx_number && <span className="font-mono">DX {court.dx_number}</span>}
            {court.distance_miles != null && <span>{court.distance_miles} mi away</span>}
          </div>
          {court.source_address && (
            <div className="text-[10px] text-slate-500 mt-1 flex items-start gap-1">
              <MapPin className="w-2.5 h-2.5 mt-0.5 shrink-0" />
              <span>Based on: {court.source_address}</span>
            </div>
          )}
          {court.url && (
            <a href={court.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] text-blue-700 hover:underline mt-1" data-testid={`issuing-court-govuk-link-${party}`}>
              <ExternalLink className="w-3 h-3" /> View on GOV.UK
            </a>
          )}
          <div className="text-[10px] text-muted-foreground mt-1.5">
            Logged {court.selected_at ? new Date(court.selected_at).toLocaleDateString("en-GB") : ""} by {court.selected_by || "Ops"}
          </div>
        </div>
      )}

      {/* Empty state — auto find + manual fallback */}
      {!court && !suggestions && !manual && (
        <div className="space-y-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => find()} disabled={finding} data-testid={`issuing-court-find-${party}`}>
              {finding ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Search className="w-3 h-3 mr-1" />}
              {findLabel}
            </Button>
            <button type="button" className="text-[11px] text-blue-700 hover:underline" onClick={() => { setManual(true); setSuggestions(null); }} data-testid={`issuing-court-manual-${party}`}>
              search / enter any court
            </button>
          </div>
          {finding && (
            <p className="text-[10px] text-muted-foreground italic">
              {party === "guarantor"
                ? "Scanning LoanDisk borrower records for addresses — this can take up to a minute…"
                : "Pulling the registered office from Companies House…"}
            </p>
          )}
        </div>
      )}

      {/* Suggestions — resolved address + nearest courts */}
      {suggestions && (
        <div className="space-y-2" data-testid={`issuing-court-suggestions-${party}`}>
          {suggestions.source && (
            <div className="text-[11px] bg-blue-50 border border-blue-200 rounded p-2 text-blue-900 flex items-start gap-1.5">
              <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
              <span>
                <strong>{suggestions.source.label}</strong><br />
                {suggestions.source.address} <span className="font-mono">({suggestions.source.postcode})</span>
              </span>
            </div>
          )}
          {/* Alternative guarantor addresses (multi-guarantor cases) */}
          {(suggestions.sources || []).length > 0 && (
            <Select onValueChange={(v) => find(Number(v))}>
              <SelectTrigger className="h-7 text-[11px]" data-testid={`issuing-court-source-picker-${party}`}>
                <SelectValue placeholder={`Other addresses (${suggestions.sources.length}) — switch person`} />
              </SelectTrigger>
              <SelectContent>
                {suggestions.sources.map(s => (
                  <SelectItem key={s.borrower_id} value={String(s.borrower_id)} disabled={!s.postcode}>
                    {s.label}{!s.postcode ? " — no postcode" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {(suggestions.courts || []).length > 0 && (
            <ul className="divide-y border rounded-md max-h-56 overflow-auto bg-white">
              {suggestions.courts.map(c => (
                <li key={c.slug || c.name} className="p-2 flex items-start gap-2 hover:bg-slate-50">
                  <Landmark className="w-3.5 h-3.5 text-slate-500 mt-0.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-semibold text-slate-800 leading-tight">{c.name}</div>
                    <div className="text-[10px] text-muted-foreground truncate">{formatCourtAddress(c) || "—"}</div>
                    <div className="text-[10px] text-slate-500">
                      {c.distance_miles != null && <span>{c.distance_miles} mi</span>}
                      {c.dx_number && <span> · DX {c.dx_number}</span>}
                    </div>
                  </div>
                  <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 shrink-0" onClick={() => save(c, suggestions.source)} disabled={busy} data-testid={`issuing-court-pick-${party}-${c.slug || "court"}`}>
                    Select
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex items-center gap-2">
            <button type="button" className="text-[11px] text-blue-700 hover:underline" onClick={() => { setManual(true); setSuggestions(null); }} data-testid={`issuing-court-any-${party}`}>
              search / enter any court instead
            </button>
            <button type="button" className="text-[11px] text-slate-500 hover:underline inline-flex items-center gap-0.5" onClick={() => setSuggestions(null)}>
              <X className="w-3 h-3" /> cancel
            </button>
          </div>
        </div>
      )}

      {/* Manual postcode / name search fallback + free-text court entry (Emma #2) */}
      {manual && (
        <div className="space-y-2">
          <CourtPicker onSelect={(c) => save(c)} />
          <ManualCourtEntry party={party} busy={busy} onSave={(c) => save(c)} />
          <button type="button" className="text-[11px] text-slate-500 hover:underline inline-flex items-center gap-0.5" onClick={() => setManual(false)}>
            <X className="w-3 h-3" /> cancel
          </button>
        </div>
      )}
    </div>
  );
}

// Emma #2 — any court can be typed in when GOV.UK doesn't list it (or the
// handler already knows the venue). Saved with `manual: true` for the audit note.
function ManualCourtEntry({ party, busy, onSave }) {
  const [open, setOpen]   = useState(false);
  const [name, setName]   = useState("");
  const [line, setLine]   = useState("");
  const [town, setTown]   = useState("");
  const [pc, setPc]       = useState("");
  const [note, setNote]   = useState("");

  if (!open) {
    return (
      <button type="button" className="text-[11px] text-blue-700 hover:underline inline-flex items-center gap-1"
        onClick={() => setOpen(true)} data-testid={`issuing-court-manual-entry-${party}`}>
        <Pencil className="w-3 h-3" /> Not listed? Enter the court manually
      </button>
    );
  }
  const submit = () => {
    if (name.trim().length < 3) { toast.error("Enter the court name"); return; }
    onSave({
      name: name.trim(),
      manual: true,
      manual_note: note.trim() || undefined,
      address: { lines: line.trim() ? [line.trim()] : [], town: town.trim() || null, postcode: pc.trim().toUpperCase() || null },
    });
  };
  return (
    <div className="border border-dashed rounded-md p-2 space-y-1.5 bg-slate-50" data-testid={`issuing-court-manual-form-${party}`}>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Manual court entry</div>
      <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Court name (required) — e.g. County Court at Barnet"
        className="h-8 text-xs" data-testid={`issuing-court-manual-name-${party}`} />
      <div className="grid grid-cols-3 gap-1.5">
        <Input value={line} onChange={(e) => setLine(e.target.value)} placeholder="Address line" className="h-7 text-[11px]" data-testid={`issuing-court-manual-line-${party}`} />
        <Input value={town} onChange={(e) => setTown(e.target.value)} placeholder="Town" className="h-7 text-[11px]" data-testid={`issuing-court-manual-town-${party}`} />
        <Input value={pc} onChange={(e) => setPc(e.target.value.toUpperCase())} placeholder="Postcode" className="h-7 text-[11px]" data-testid={`issuing-court-manual-postcode-${party}`} />
      </div>
      <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why this court? (optional — e.g. claim already issued here)"
        className="h-7 text-[11px]" data-testid={`issuing-court-manual-note-${party}`} />
      <div className="flex items-center gap-2">
        <Button size="sm" className="h-7 text-[11px]" onClick={submit} disabled={busy} data-testid={`issuing-court-manual-save-${party}`}>
          Use this court
        </Button>
        <button type="button" className="text-[11px] text-slate-500 hover:underline" onClick={() => setOpen(false)}>close</button>
      </div>
    </div>
  );
}


// PG label ("which guarantee does this court relate to") + claim / case
// number, saved on blur — Emma's asks on the legal case file.
function CourtMeta({ party, court, caseRow, onChanged }) {
  const [label, setLabel] = useState(court.party_label || "");
  const [claim, setClaim] = useState(court.claim_number || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setLabel(court.party_label || ""); setClaim(court.claim_number || ""); }, [court.party_label, court.claim_number]);
  const save = async (patch) => {
    setSaving(true);
    try { await patchCaseIssuingCourtMeta(caseRow.case_id, party, patch); await onChanged?.(); }
    catch (e) { toast.error("Save failed: " + (e?.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };
  const guarantors = (caseRow.guarantors || caseRow.contacts || []).map((g) => g.name || g.full_name).filter(Boolean);
  return (
    <div className="grid grid-cols-2 gap-2 mb-2" data-testid={`issuing-court-meta-${party}`}>
      {party !== "company" && (
        <div>
          <label className="text-[9px] uppercase tracking-wider text-slate-500">Relates to (PG)</label>
          <Input list={`pg-names-${party}`} value={label} onChange={(e) => setLabel(e.target.value)}
            onBlur={() => label !== (court.party_label || "") && save({ party_label: label })}
            placeholder="e.g. PG — Harry Wollen" className="h-7 text-xs" data-testid={`issuing-court-pg-label-${party}`} />
          <datalist id={`pg-names-${party}`}>{guarantors.map((n) => <option key={n} value={`PG — ${n}`} />)}</datalist>
        </div>
      )}
      <div className={party === "company" ? "col-span-2" : ""}>
        <label className="text-[9px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><Hash className="w-2.5 h-2.5" />Claim / case number{saving && <Loader2 className="w-2.5 h-2.5 animate-spin" />}</label>
        <Input value={claim} onChange={(e) => setClaim(e.target.value)}
          onBlur={() => claim !== (court.claim_number || "") && save({ claim_number: claim })}
          placeholder="e.g. K0AB1234 / 1001 of 2026" className="h-7 text-xs font-mono" data-testid={`issuing-court-claim-${party}`} />
      </div>
    </div>
  );
}
